// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import PropertyBanner from 'components/organisms/propertyManagementBanner';
import VisionPhilosophySection from 'components/templates/vision&PhilosophySection';
import { FooterSelector } from 'modules/footerLink/selector';
import { FooterActions } from 'modules/footerLink/actions';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const PropertyManagementPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getPageData,
    visionPhilosophySection,
    getSiteSettings,
    footerLink,
    getFooterLinkData,
    siteSettings,
    contact,
    getContactDetails,
    getBannerData,
    propertyBanner,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');

  useEffect(() => {
    getBannerData();
    getContactDetails();
    getVerticals();
    getPageData();
    getFooterLinkData();
    getSiteSettings();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !visionPhilosophySection ||
    !propertyBanner ||
    !footerLink
  ) {
    return null;
  }

  const componentList = [
    ...(!isEmpty(propertyBanner)
      ? [
          {
            component: PropertyBanner,
            data: propertyBanner,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Property Management',
                breadcrumbLink: RouteNames.property,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(visionPhilosophySection.visionSection)
      ? [
          {
            component: VisionPhilosophySection,
            data: { visionPhilosophySection },
            mobileBannerData: visionPhilosophySection.visionSectionMobile,
            theme: 'light-theme',
            sectionId: 'vision-and-philosophy',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Property Management',
                breadcrumbLink: RouteNames.property,
              },
              {
                breadcrumbName: 'Vision & Philosophy',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: {
        contact,
        verticals,
        verticalId,
        showVerticalDropdown: false,
        projectName: 'Property management',
        type: visionPhilosophySection.visionSectionMobile && 'no-padding',
      },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Property Management',
          breadcrumbLink: RouteNames.property,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Property Management',
          breadcrumbLink: RouteNames.property,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <PageTemplate
      theme={theme}
      componentList={componentList}
      updateTheme={e => setTheme(e)}
      verticals={verticals}
      siteSettings={siteSettings}
    />
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      FooterSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state),
    contact: HomeSelector.getContactForMenuItems(state, 'Property management'),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    visionPhilosophySection: CmsPagesSelector.getVisionPhilosophy(
      state,
      'property',
      '',
      false
    ),
    siteSettings: HomeSelector.getSiteSettings(state),
    propertyBanner: HomeSelector.getBanners(
      state,
      false,
      '',
      'Property management'
    ),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getBannerData,
    getContactDetails,
    getVerticals,
    getSiteSettings,
  } = HomeActions;
  const { getFooterLinkData } = FooterActions;
  const { getPageData } = CmsPagesActions;
  return bindActionCreators(
    {
      getBannerData,
      getContactDetails,
      getVerticals,
      getPageData,
      getFooterLinkData,
      getSiteSettings,
    },
    dispatch
  );
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PropertyManagementPage);
